import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Typography,
  Divider,
  CircularProgress,
  Alert,
  Snackbar
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { providerService } from '../../services/providerService';

// Esquema de validación para el formulario de cuenta bancaria
const accountSchema = Yup.object().shape({
  number: Yup.string()
    .matches(
      /^ES\d{22}$/,
      'El número de cuenta debe tener formato IBAN español válido (ej: ES7921000813610123456789)'
    )
    .required('El número de cuenta es obligatorio')
});

function AccountsModal({ open, onClose, provider, onSave }) {
  const [accounts, setAccounts] = useState([]);
  const [editingAccount, setEditingAccount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Cargar cuentas cuando se abre el modal
  useEffect(() => {
    if (open && provider) {
      setAccounts(provider.accounts || []);
    }
  }, [open, provider]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const formik = useFormik({
    initialValues: {
      number: ''
    },
    validationSchema: accountSchema,
    onSubmit: async (values) => {
      setSubmitting(true);
      
      try {
        if (editingAccount) {
          // Para editar, necesitaríamos un endpoint específico
          // Por ahora, añadimos como nueva y eliminamos la vieja
          await handleDelete(editingAccount.id, false);
          const result = await providerService.addAccount(provider.id, values);
          
          if (result.success) {
            const updatedProvider = await providerService.getProvider(provider.id);
            if (updatedProvider.success) {
              setAccounts(updatedProvider.data.accounts || []);
              showSnackbar('Cuenta actualizada correctamente');
            }
          } else {
            showSnackbar('Error al actualizar cuenta: ' + result.message, 'error');
          }
        } else {
          // Añadir nueva cuenta
          const result = await providerService.addAccount(provider.id, values);
          
          if (result.success) {
            const updatedProvider = await providerService.getProvider(provider.id);
            if (updatedProvider.success) {
              setAccounts(updatedProvider.data.accounts || []);
              showSnackbar('Cuenta añadida correctamente');
            }
          } else {
            showSnackbar('Error al añadir cuenta: ' + result.message, 'error');
          }
        }
        
        formik.resetForm();
        setEditingAccount(null);
      } catch (error) {
        showSnackbar('Error al procesar cuenta', 'error');
      } finally {
        setSubmitting(false);
      }
    }
  });

  const handleEdit = (account) => {
    setEditingAccount(account);
    formik.setValues(account);
  };

  const handleDelete = async (accountId, showMessage = true) => {
    if (showMessage && !window.confirm('¿Estás seguro de que deseas eliminar esta cuenta bancaria?')) {
      return;
    }

    try {
      const result = await providerService.deleteAccount(accountId);
      
      if (result.success) {
        const updatedProvider = await providerService.getProvider(provider.id);
        if (updatedProvider.success) {
          setAccounts(updatedProvider.data.accounts || []);
          if (showMessage) {
            showSnackbar('Cuenta eliminada correctamente');
          }
        }
      } else {
        showSnackbar('Error al eliminar cuenta: ' + result.message, 'error');
      }
    } catch (error) {
      showSnackbar('Error al eliminar cuenta', 'error');
    }
  };

  const handleClose = () => {
    formik.resetForm();
    setEditingAccount(null);
    onSave(accounts); // Notificar cambios al componente padre
    onClose();
  };

  // Función para formatear el número de cuenta IBAN
  const formatIBAN = (iban) => {
    if (!iban) return '';
    return iban.replace(/(.{4})/g, '$1 ').trim();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Cuentas Bancarias de {provider?.name}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            {/* Formulario de cuenta bancaria */}
            <Grid item xs={12}>
              <form onSubmit={formik.handleSubmit}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={11}>
                    <TextField
                      fullWidth
                      name="number"
                      label="Número de Cuenta (IBAN)"
                      value={formik.values.number}
                      onChange={(e) => {
                        // Eliminar espacios y convertir a mayúsculas
                        const value = e.target.value.replace(/\s/g, '').toUpperCase();
                        formik.setFieldValue('number', value);
                      }}
                      error={formik.touched.number && Boolean(formik.errors.number)}
                      helperText={formik.touched.number && formik.errors.number}
                      placeholder="ES7921000813610123456789"
                      disabled={submitting}
                    />
                  </Grid>
                  <Grid item xs={12} sm={1}>
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      sx={{ height: '100%' }}
                      disabled={submitting}
                    >
                      {submitting ? <CircularProgress size={20} /> : <AddIcon />}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Grid>

            {/* Lista de cuentas */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Cuentas Bancarias ({accounts.length})
              </Typography>
              <List>
                {accounts.map((account, index) => (
                  <React.Fragment key={account.id}>
                    <ListItem>
                      <ListItemText
                        primary={formatIBAN(account.number)}
                        secondary="Cuenta IBAN"
                      />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          aria-label="editar"
                          onClick={() => handleEdit(account)}
                          sx={{ mr: 1 }}
                          disabled={submitting}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          edge="end"
                          aria-label="eliminar"
                          onClick={() => handleDelete(account.id)}
                          disabled={submitting}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                    {index < accounts.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={submitting}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default AccountsModal; 